import { useConsoleTheme } from "@/hooks/useConsoleTheme";
import { cn } from "@/lib/utils";

export type ConsoleTab = "market" | "usage" | "account" | "recharge";

interface SidebarProps {
  active: ConsoleTab;
  onChange: (tab: ConsoleTab) => void;
}

const TABS: Array<{ id: ConsoleTab; label: string; icon: React.ReactNode }> = [
  {
    id: "market",
    label: "应用市场",
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 8.5l1.6-4.5h14.8L21 8.5" />
        <path d="M3 8.5h18v3a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0v-3z" />
        <path d="M5 11.5V20h14V11.5" />
        <path d="M10 20v-5h4v5" />
      </svg>
    ),
  },
  {
    id: "usage",
    label: "用量统计",
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="7" height="9" rx="1.5" />
        <rect x="14" y="3" width="7" height="5" rx="1.5" />
        <rect x="14" y="12" width="7" height="9" rx="1.5" />
        <rect x="3" y="16" width="7" height="5" rx="1.5" />
      </svg>
    ),
  },
  {
    id: "account",
    label: "我的账户",
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
  },
  {
    id: "recharge",
    label: "余额充值",
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2.5" y="6" width="19" height="13" rx="2.5" />
        <path d="M2.5 10h19" />
        <path d="M6 15.5h3" />
      </svg>
    ),
  },
];

export function Sidebar({ active, onChange }: SidebarProps) {
  const { theme, toggle } = useConsoleTheme();

  return (
    <aside className="c2-side">
      <a className="c2-brand" href="/">
        <span className="c2-brand-logo" aria-hidden="true">
          <svg viewBox="0 0 64 64" width="36" height="36">
            <defs>
              <linearGradient id="navLogoG" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="50%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#7b61ff" />
              </linearGradient>
            </defs>
            <rect x="2" y="2" width="60" height="60" rx="14" fill="url(#navLogoG)" />
            <path d="M 18 50 L 32 16 L 46 50 M 23 38 L 41 38" stroke="#0a0a0f" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </span>
        <span className="c2-brand-name">Agentfly</span>
      </a>

      <nav className="c2-nav" aria-label="控制台导航">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={cn("c2-nav-item", active === t.id && "active")}
            type="button"
            onClick={() => onChange(t.id)}
          >
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      <div className="c2-side-foot">
        <button
          className="c2-theme-btn"
          type="button"
          aria-pressed={theme === "dark"}
          onClick={toggle}
        >
          <span className="c2-theme-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.5A8.5 8.5 0 1 1 11.5 3a6.5 6.5 0 0 0 9.5 9.5z" />
            </svg>
          </span>
          <span>{theme === "dark" ? "浅色模式" : "深色模式"}</span>
        </button>
      </div>
    </aside>
  );
}

export const TAB_TITLES: Record<ConsoleTab, [string, string]> = {
  market:   ["应用市场", "挑一个智能体，一键安装或一键接入，复制即用。"],
  usage:    ["用量统计", "按时间范围查看花费、请求数、模型与 Token 使用情况。"],
  account:  ["我的账户", "管理 API Key、Base URL、用户名和密码。"],
  recharge: ["账户充值", "选择充值套餐，生成人工处理的充值申请。"],
};
